import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PopupNames } from 'core';
import {
  useAllFakturowniaInvoices,
  useLmsAccessBlock,
  usePopupStateOperations,
  useStudentPermissions,
} from 'core/hooks';
import { LmsBlockModal } from 'molecules';
import type { IStudentAlertTarget } from 'core/utils/studentAlerts';

import * as S from '../FakturowniaInvoices/FakturowniaInvoices.styled';
import * as L from './StudentDetail.styled';
import { IStudent } from './parts';
import { IdentityAnchor } from './parts/IdentityAnchor';
import { MoveToGroupInlinePopup } from './parts/MoveToGroupInlinePopup';
import { MarkPaidManuallyInlinePopup } from './parts/MarkPaidManuallyInlinePopup';
import { PickInvoiceForContributionPopup } from './parts/PickInvoiceForContributionPopup';
import {
  contributionStatusOf,
  isPaidContributionStatus,
} from './parts/contributionStatus';

interface IHeaderBarProps {
  student: IStudent;
  /** Scroll to the orphan-invoices block (Link Invoice action). */
  onScrollToOrphans?: () => void;
  /** Atomic deep-link navigation, used by the data-driven Link Invoice action. */
  onNavigate?: (target: IStudentAlertTarget) => void;
}

/**
 * Student card header: identity anchor + name/email/phone, and a row of
 * role-and-data gated quick actions (Area 4, Req 9–13).
 *
 * - «Link Invoice» appears only when a FRESH orphan-invoice lookup finds ≥1
 *   orphan (no hardcoded flag, Req 9.1–9.3); selecting it deep-links to the
 *   orphan block, navigating even if none currently exist (Req 9.4).
 * - Quick actions (add agreement, record payment, issue invoice, change group,
 *   contact by email) open existing flows without leaving the card (Req 10.8);
 *   each is hidden when the operator's role lacks the capability (Req 10.7) and
 *   money actions are disabled without an active agreement (Req 11).
 */
export const HeaderBar = memo(
  ({ student, onScrollToOrphans, onNavigate }: IHeaderBarProps) => {
    const { t } = useTranslation();
    const {
      operations: { showPopup },
    } = usePopupStateOperations();
    const permissions = useStudentPermissions();
    const lmsBlock = useLmsAccessBlock(student.id);
    const [moving, setMoving] = useState(false);
    const [recordingPayment, setRecordingPayment] = useState(false);
    const [issuingInvoice, setIssuingInvoice] = useState(false);
    const [lmsModal, setLmsModal] = useState<'block' | 'unblock' | null>(null);

    const fullName = useMemo(
      () =>
        [
          student.user?.firstName,
          student.user?.middleName,
          student.user?.lastName,
        ]
          .filter(Boolean)
          .join(' ') || '—',
      [student.user],
    );

    const email = student.user?.email || '';

    // Fresh orphan-invoice presence on every render — no cached flag (Req 9.3).
    // Spec 0004: «orphan» now means a linkable invoice without a взнос.
    const { data: invoices } = useAllFakturowniaInvoices({
      studentId: student.id,
      direction: 'issued',
    });
    const hasOrphanInvoices = useMemo(
      () =>
        invoices.some(
          (r) => !r.contribution && !r.isCorrection && !r.isQuotation,
        ),
      [invoices],
    );

    const activeAgreements = useMemo(
      () => (student.agreements || []).filter((a) => a && !a.terminated),
      [student.agreements],
    );
    const hasActiveAgreement = activeAgreements.length > 0;
    const activeAgreement = activeAgreements[0] || null;

    // Schedule-first model (spec 0004): the header «Отметить оплаченным» /
    // «Выставить счёт» shortcuts act on the next unpaid взнос of the (first)
    // active agreement; per-row control lives in the agreement's
    // PaymentsBlock. «Unpaid» is the backend status ≠ paid/paid_manual.
    const nextUnpaid = useMemo(() => {
      if (!activeAgreement) return null;
      const unpaid = (activeAgreement.contributions?.edges || [])
        .map((e) => e?.node)
        .filter(
          (n): n is NonNullable<typeof n> =>
            Boolean(n) && !isPaidContributionStatus(contributionStatusOf(n!)),
        );
      unpaid.sort((a, b) => (a.date || '').localeCompare(b.date || ''));
      return unpaid[0] || null;
    }, [activeAgreement]);

    // «+ Договор» is the only one that creates a new agreement.
    const onAddAgreement = useCallback(() => {
      showPopup(PopupNames.studentPayment, { studentId: student.id });
    }, [showPopup, student.id]);

    // «Отметить оплаченным» → manual-mark popup for the next unpaid взнос.
    const onRecordPayment = useCallback(() => setRecordingPayment(true), []);

    // «Выставить счёт» → attach a Fakturownia invoice to that contribution
    // (invoices originate in Fakturownia; the ERP links/issues against a row).
    const onIssueInvoice = useCallback(() => setIssuingInvoice(true), []);

    const moneyDisabledTitle = !hasActiveAgreement
      ? t('pages.studentDetail.actions.noActiveAgreement')
      : !nextUnpaid
      ? t('pages.studentDetail.actions.noUnpaidContribution')
      : undefined;

    const onLinkInvoice = useCallback(() => {
      if (onNavigate) onNavigate({ tab: 'overview', block: 'orphanInvoices' });
      else if (onScrollToOrphans) onScrollToOrphans();
    }, [onNavigate, onScrollToOrphans]);

    // Block / unblock LMS access (debtor block, spec 0003). The block itself is
    // a manual confirmation of a system-detected debtor; restore is automatic
    // once the debt clears, but the operator can also lift it by hand here.
    const onToggleLmsBlock = useCallback(() => {
      setLmsModal(lmsBlock.isBlocked ? 'unblock' : 'block');
    }, [lmsBlock.isBlocked]);

    const onLmsModalSubmit = useCallback(
      async (reason?: string) => {
        const ok =
          lmsModal === 'unblock'
            ? await lmsBlock.unblock()
            : await lmsBlock.block(reason || undefined);
        if (ok) setLmsModal(null);
      },
      [lmsModal, lmsBlock],
    );

    // Change-group: the single active agreement is moved directly; with more
    // than one the operator picks which agreement via the inline popup (Req 11.3).
    const moveAgreement = activeAgreement;

    return (
      <>
        <S.HeaderRow>
          <IdentityRow>
            <IdentityAnchor fullName={fullName} />
            <S.PageTitle>{fullName}</S.PageTitle>
          </IdentityRow>
          <L.HeaderActions>
            {permissions.canAddAgreement ? (
              <L.PrimaryActionButton
                type="button"
                tone="primary"
                onClick={onAddAgreement}
              >
                {t('pages.studentDetail.actions.addAgreement')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canRecordPayment ? (
              <L.PrimaryActionButton
                type="button"
                onClick={onRecordPayment}
                disabled={!hasActiveAgreement || !nextUnpaid}
                title={moneyDisabledTitle}
              >
                {t('pages.studentDetail.actions.markPaid')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canIssueInvoice ? (
              <L.PrimaryActionButton
                type="button"
                onClick={onIssueInvoice}
                disabled={!hasActiveAgreement || !nextUnpaid}
                title={moneyDisabledTitle}
              >
                {t('pages.studentDetail.actions.issueInvoice')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canChangeGroup && moveAgreement ? (
              <L.PrimaryActionButton
                type="button"
                onClick={() => setMoving(true)}
              >
                {t('pages.studentDetail.actions.changeGroup')}
              </L.PrimaryActionButton>
            ) : null}

            {hasOrphanInvoices ? (
              <L.PrimaryActionButton type="button" onClick={onLinkInvoice}>
                {t('pages.studentDetail.actions.linkInvoice')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canContactEmail && email ? (
              <L.PrimaryActionButton
                as="a"
                href={`mailto:${email}`}
                tone={undefined}
              >
                {t('pages.studentDetail.actions.contactEmail')}
              </L.PrimaryActionButton>
            ) : null}

            {permissions.canBlockLmsAccess ? (
              <L.PrimaryActionButton
                type="button"
                onClick={onToggleLmsBlock}
                disabled={lmsBlock.loading}
                title={
                  lmsBlock.isBlocked && lmsBlock.reason
                    ? lmsBlock.reason
                    : undefined
                }
              >
                {lmsBlock.isBlocked
                  ? t('pages.studentDetail.lmsBlock.unblock')
                  : t('pages.studentDetail.lmsBlock.block')}
              </L.PrimaryActionButton>
            ) : null}
          </L.HeaderActions>
        </S.HeaderRow>
        <S.SubTitle>
          {email || '—'}
          {student.user?.phone ? ` · ${student.user.phone}` : ''}
        </S.SubTitle>

        {moving && moveAgreement ? (
          <MoveToGroupInlinePopup
            agreement={moveAgreement}
            studentId={student.id}
            onClose={() => setMoving(false)}
          />
        ) : null}

        {recordingPayment && activeAgreement && nextUnpaid ? (
          <MarkPaidManuallyInlinePopup
            contribution={nextUnpaid}
            agreement={activeAgreement}
            studentId={student.id}
            onClose={() => setRecordingPayment(false)}
            onSaved={() => setRecordingPayment(false)}
          />
        ) : null}

        {issuingInvoice && activeAgreement && nextUnpaid ? (
          <PickInvoiceForContributionPopup
            contribution={nextUnpaid}
            agreement={activeAgreement}
            studentId={student.id}
            studentEmail={email || null}
            onClose={() => setIssuingInvoice(false)}
            onLinked={() => setIssuingInvoice(false)}
          />
        ) : null}

        {lmsModal ? (
          <LmsBlockModal
            key={lmsModal}
            mode={lmsModal}
            loading={lmsBlock.loading}
            onSubmit={onLmsModalSubmit}
            onCancel={() => setLmsModal(null)}
          />
        ) : null}
      </>
    );
  },
);

const IdentityRow = ({ children }: { children: React.ReactNode }) => (
  <span
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 12,
    }}
  >
    {children}
  </span>
);
